import Head from 'next/head';
import Link from 'next/link';
import { Component } from 'react';
import { OptionPathToUrlMapper } from 'xconvert-web-shared';

import {
    getMediaToolConfigBySlug,
    MediaToolConfig,
    processTypeConfig,
} from '../../commons/media-tools.config';
import { FORMAT_HUBS_BY_MEDIATYPE as SITE_NAV_FORMAT_HUBS } from '../../commons/site-nav';
import styles from './media-tools-page.module.scss';

/**
 * Types for selector-options.json structure
 */
interface OptionItem {
    value: string;
    name: string;
}

interface MediaOption {
    mediaType: string;
    mediaName: string;
    options: OptionItem[];
    toOptions?: MediaOption[];
}

interface SelectorOption {
    process: string;
    processName: string;
    toOnlyMediaOptions?: MediaOption[];
    fromToMediaOptions?: MediaOption[];
}

/**
 * Process type icons for display
 */
const processIcons: Record<string, string> = {
    'CONVERT': '🔄',
    'COMPRESS': '📦',
    'MERGE': '🔗',
    'SPLIT': '✂️',
    'CUT': '✂️',
    'TRIM': '✂️',
    'CROP': '🖼️',
    'RESIZE': '📐',
    'ROTATE': '🔃',
    'FLIP': '↔️',
    'EXTRACT': '📤',
    'ARCHIVE': '🗜️',
    'ENCODE': '🔐',
    'DECODE': '🔓',
    'GENERATE': '✨',
    'COUNT': '🔢',
    'CALCULATE': '🧮',
    'LOOKUP': '🔎',
    'FORMAT': '📝',
    'MINIFY': '🗜️',
    'VALIDATE': '✅',
    'TEST': '🧪',
    // S6 family #10 — keys MUST match xconfig ProcessType enum values exactly
    // (no hyphens) so this map keys consistently with processTypeConfig and
    // the selector-options.json `process` field.
    'WATERMARK': '💧',
    'ADDPAGENUMBERS': '🔢',
    'DELETEPAGES': '✂️',
    'EXTRACTPAGES': '📄',
    'REORDER': '🔀',
    'NUP': '📑',
    'FLATTEN': '📚',
};

/**
 * Feature item generated from selector-options.json
 */
export interface FeatureItem {
    title: string;
    link: string;
    sourceFormat?: string; // For grouping conversions by source format
}

export interface ProcessSection {
    processType: string;
    displayName: string;
    icon: string;
    order: number;
    features: FeatureItem[];
    titleOverride?: string; // full section title; bypasses the "{displayName} Tools" template
}

export interface MediaToolsPageProps {
    config: MediaToolConfig;
    sections: ProcessSection[];
}

interface MediaToolsPageState {
    searchQuery: string;
    expandedGroups: Set<string>;
}

/** Number of items to show before collapsing */
const INITIAL_VISIBLE_COUNT = 6;
/** Threshold for when to enable grouping */
const GROUP_THRESHOLD = 20;

/**
 * Generates a URL-friendly slug from a format name
 */
function formatToSlug(format: string): string {
    return format.toLowerCase().replace(/\./g, '-');
}

/**
 * Generates a readable title from format names. Uses processTypeConfig's
 * displayName when available so multi-word process names render as
 * "Add Page Numbers PDF" instead of "Addpagenumbers PDF".
 */
function generateTitle(process: string, from: string, to?: string): string {
    const processKey = process.toUpperCase();
    const processName = processTypeConfig[processKey]?.displayName
        ?? (process.charAt(0).toUpperCase() + process.slice(1).toLowerCase());
    const fromName = from.toUpperCase();

    if (to) {
        const toName = to.toUpperCase();
        return `${processName} ${fromName} to ${toName}`;
    }
    return `${processName} ${fromName}`;
}

/**
 * Generates a link URL based on process type and formats. Routes through
 * OptionPathToUrlMapper so process types with override mappings (e.g.
 * nup-pdf → n-up-pdf, addpagenumbers-pdf → add-page-numbers-pdf,
 * extractpages-pdf → extract-pdf-pages) resolve to their canonical pretty URLs.
 */
function generateLink(process: string, from: string, to?: string): string {
    const processSlug = process.toLowerCase();
    const fromSlug = formatToSlug(from);
    const optionPath = to
        ? `${processSlug}-${fromSlug}-to-${formatToSlug(to)}`
        : `${processSlug}-${fromSlug}`;
    return `/${OptionPathToUrlMapper.mapToUrlPath(optionPath)}`;
}

/**
 * Generates features from toOnlyMediaOptions (COMPRESS, CROP, RESIZE, etc.)
 * These are single-file operations: /{process}-{format}
 */
function generateFeaturesFromToOnlyOptions(
    process: string,
    mediaOptions: MediaOption[],
    targetMediaTypes: Set<string>
): FeatureItem[] {
    const features: FeatureItem[] = [];

    mediaOptions.forEach(mediaOption => {
        if (!targetMediaTypes.has(mediaOption.mediaType.toUpperCase())) {
            return;
        }

        mediaOption.options.forEach(item => {
            const format = item.value;
            if (['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'ARCHIVE', 'FILE'].includes(format.toUpperCase())) {
                return;
            }

            const link = generateLink(process, format);
            features.push({
                title: generateTitle(process, format),
                link,
                sourceFormat: format.toUpperCase(),
            });
        });
    });

    return features;
}

/**
 * Generates features from fromToMediaOptions (CONVERT, MERGE)
 * These are conversion operations: /{process}-{from}-to-{to}
 */
function generateFeaturesFromFromToOptions(
    process: string,
    mediaOptions: MediaOption[],
    targetMediaTypes: Set<string>
): FeatureItem[] {
    const features: FeatureItem[] = [];

    mediaOptions.forEach(mediaOption => {
        // Check if this "from" media type matches our target
        const fromMatchesTarget = targetMediaTypes.has(mediaOption.mediaType.toUpperCase());

        mediaOption.options.forEach(fromItem => {
            const fromFormat = fromItem.value;
            if (['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'ARCHIVE', 'FILE'].includes(fromFormat.toUpperCase())) {
                return;
            }

            mediaOption.toOptions?.forEach(toOption => {
                const toMatchesTarget = targetMediaTypes.has(toOption.mediaType.toUpperCase());

                if (!fromMatchesTarget && !toMatchesTarget) {
                    return;
                }

                toOption.options.forEach(toItem => {
                    const toFormat = toItem.value;
                    if (['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'ARCHIVE', 'FILE'].includes(toFormat.toUpperCase())) {
                        return;
                    }
                    if (fromFormat.toUpperCase() === toFormat.toUpperCase()) {
                        return;
                    }

                    const link = generateLink(process, fromFormat, toFormat);
                    features.push({
                        title: generateTitle(process, fromFormat, toFormat),
                        link,
                        sourceFormat: fromFormat.toUpperCase(),
                    });
                });
            });
        });
    });

    return features;
}

/**
 * Generates props for a media tools page based on the slug.
 * Creates features directly from selector-options.json structure.
 */

/**
 * Per-format converter hubs (the extension-level converter tier, e.g. /mp4-converter), surfaced as a prominent
 * "Format Converters" section so the category pages link to every hub. The lists are xconvert-web-ssr's
 * sources/site-nav.json `formatHubs` (commons/site-nav.ts; HANDOFF-L4 H5, 2026-10-11), mapped to FeatureItem.
 */
const FORMAT_HUBS_BY_MEDIATYPE: Record<string, FeatureItem[]> = Object.fromEntries(
    Object.entries(SITE_NAV_FORMAT_HUBS).map(([mt, links]) => [mt, links.map(l => ({ title: l.text, link: l.href }))])
);

export function generateMediaToolsProps(
    slug: string,
    selectorOptions: SelectorOption[]
): MediaToolsPageProps | null {
    const config = getMediaToolConfigBySlug(slug);

    if (!config) {
        return null;
    }

    const targetMediaTypes = new Set(config.mediaTypes.map(mt => mt.toUpperCase()));
    const sectionMap = new Map<string, FeatureItem[]>();

    // Utility tools — not in selector-options, hardcoded here
    if (targetMediaTypes.has('TOOL')) {
        const utilityTools: FeatureItem[] = [
            { title: 'Color Converter', link: '/color-converter' },
            { title: 'Number Base Converter', link: '/number-base-converter' },
            { title: 'Text Case Converter', link: '/text-case-converter' },
            { title: 'JSON to YAML', link: '/convert-json-to-yaml' },
            { title: 'Hash Generator', link: '/hash-generator' },
            { title: 'Image to Text (OCR)', link: '/image-to-text' },
            { title: 'Markdown to HTML', link: '/convert-markdown-to-html' },
            { title: 'Lorem Ipsum Generator', link: '/lorem-ipsum-generator' },
        ];
        sectionMap.set('UTILITY', utilityTools);
    }

    selectorOptions.forEach(option => {
        const process = option.process.toUpperCase();
        let features: FeatureItem[] = [];

        // Handle toOnlyMediaOptions (COMPRESS, CROP, RESIZE, SPLIT, CUT, TRIM, etc.)
        if (option.toOnlyMediaOptions) {
            features = generateFeaturesFromToOnlyOptions(
                option.process,
                option.toOnlyMediaOptions,
                targetMediaTypes
            );
        }

        // Handle fromToMediaOptions (CONVERT, MERGE, ARCHIVE)
        if (option.fromToMediaOptions) {
            features = generateFeaturesFromFromToOptions(
                option.process,
                option.fromToMediaOptions,
                targetMediaTypes
            );
        }

        if (features.length > 0) {
            if (!sectionMap.has(process)) {
                sectionMap.set(process, []);
            }
            sectionMap.get(process)!.push(...features);
        }
    });

    // Convert to sections array with display info
    const sections: ProcessSection[] = Array.from(sectionMap.entries())
        .map(([processType, features]) => ({
            processType,
            displayName: processTypeConfig[processType]?.displayName || processType,
            icon: processIcons[processType] || '🔧',
            order: processTypeConfig[processType]?.order || 99,
            features: features.sort((a, b) => a.title.localeCompare(b.title)),
        }))
        .sort((a, b) => a.order - b.order);

    // Surface the per-format converter hub tier as a prominent curated section,
    // placed just above the convert-pair section. Keeps the new /{ext}-converter
    // hubs linked from the high-authority category pages, not only the mesh.
    const formatHubFeatures: FeatureItem[] = [];
    for (const mt of targetMediaTypes) {
        if (FORMAT_HUBS_BY_MEDIATYPE[mt]) formatHubFeatures.push(...FORMAT_HUBS_BY_MEDIATYPE[mt]);
    }
    if (formatHubFeatures.length > 0) {
        const convertOrder = processTypeConfig['CONVERT']?.order ?? 30;
        sections.push({
            processType: 'FORMAT_HUB',
            displayName: 'Format Converter',
            titleOverride: 'Format Converters',
            icon: '🔄',
            order: convertOrder - 0.5, // immediately above the convert-pair section
            features: formatHubFeatures, // curated order — no alpha sort
        });
        sections.sort((a, b) => a.order - b.order);
    }

    return { config, sections };
}

/**
 * Groups features by their source format
 */
function groupFeaturesBySource(features: FeatureItem[]): Map<string, FeatureItem[]> {
    const groups = new Map<string, FeatureItem[]>();

    features.forEach(feature => {
        const source = feature.sourceFormat || 'Other';
        if (!groups.has(source)) {
            groups.set(source, []);
        }
        groups.get(source)!.push(feature);
    });

    // Sort groups by number of features (most popular first)
    return new Map(
        Array.from(groups.entries())
            .sort((a, b) => b[1].length - a[1].length)
    );
}

/**
 * Shared Media Tools Page Component
 *
 * Renders a page showing all tools for a specific media type,
 * organized by process type (Convert, Compress, etc.)
 * Features grouping by source format and collapsible sections for large lists.
 */
export default class MediaToolsPageComponent extends Component<MediaToolsPageProps, MediaToolsPageState> {
    private searchDebounceTimer: any;
    /** Pre-computed lowercase search strings: [titleLower, descriptionLower] per feature id */
    private searchIndex: Map<string, string> = new Map();

    constructor(props: MediaToolsPageProps) {
        super(props);
        this.state = {
            searchQuery: '',
            expandedGroups: new Set(),
        };
        this.buildSearchIndex(props.sections);
    }

    private buildSearchIndex(sections: ProcessSection[]) {
        this.searchIndex = new Map();
        for (const section of sections) {
            for (const f of section.features) {
                this.searchIndex.set(f.link, f.title.toLowerCase());
            }
        }
    }

    handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = setTimeout(() => {
            this.setState({ searchQuery: value });
        }, 250);
    };

    clearSearch = () => {
        clearTimeout(this.searchDebounceTimer);
        const input = document.querySelector(`.${styles['search-input']}`) as HTMLInputElement;
        if (input) input.value = '';
        this.setState({ searchQuery: '' });
    };

    componentWillUnmount() {
        clearTimeout(this.searchDebounceTimer);
    }

    toggleGroup = (groupKey: string) => {
        this.setState(prevState => {
            const newExpanded = new Set(prevState.expandedGroups);
            if (newExpanded.has(groupKey)) {
                newExpanded.delete(groupKey);
            } else {
                newExpanded.add(groupKey);
            }
            return { expandedGroups: newExpanded };
        });
    };

    filterFeatures(features: FeatureItem[]): FeatureItem[] {
        const query = this.state.searchQuery.toLowerCase().trim();
        if (!query) return features;

        return features.filter(feature => {
            const indexed = this.searchIndex.get(feature.link);
            return indexed && indexed.includes(query);
        });
    }

    renderFeatureCard(feature: FeatureItem) {
        return (
            <Link
                key={feature.link}
                href={feature.link}
                className={styles['feature-card']}
            >
                <div className={styles['feature-title']}>{feature.title}</div>
            </Link>
        );
    }

    renderGroupedSection(section: ProcessSection, filteredFeatures: FeatureItem[]) {
        const groups = groupFeaturesBySource(filteredFeatures);
        const sectionKey = section.processType;

        return (
            <div className={styles['grouped-features']}>
                {Array.from(groups.entries()).map(([sourceFormat, groupFeatures]) => {
                    const groupKey = `${sectionKey}-${sourceFormat}`;
                    const isExpanded = this.state.expandedGroups.has(groupKey);
                    const visibleFeatures = isExpanded
                        ? groupFeatures
                        : groupFeatures.slice(0, INITIAL_VISIBLE_COUNT);
                    const hiddenCount = groupFeatures.length - INITIAL_VISIBLE_COUNT;

                    return (
                        <div key={groupKey} className={styles['format-group']}>
                            <h3 className={styles['group-title']}>
                                From {sourceFormat}
                                <span className={styles['group-count']}>
                                    {groupFeatures.length} tool{groupFeatures.length !== 1 ? 's' : ''}
                                </span>
                            </h3>
                            <div className={styles['feature-grid']}>
                                {visibleFeatures.map(feature => this.renderFeatureCard(feature))}
                            </div>
                            {hiddenCount > 0 && (
                                <button
                                    className={styles['show-more-btn']}
                                    onClick={() => this.toggleGroup(groupKey)}
                                >
                                    {isExpanded
                                        ? '← Show less'
                                        : `Show ${hiddenCount} more ${sourceFormat} conversion${hiddenCount !== 1 ? 's' : ''} →`
                                    }
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderFlatSection(filteredFeatures: FeatureItem[]) {
        return (
            <div className={styles['feature-grid']}>
                {filteredFeatures.map(feature => this.renderFeatureCard(feature))}
            </div>
        );
    }

    render() {
        const { config, sections } = this.props;
        const { searchQuery } = this.state;

        const totalTools = sections.reduce((sum, s) => sum + s.features.length, 0);

        // Pre-compute filtered results once per render, cap at 50 during search
        const MAX_SEARCH_RESULTS = 50;
        const filteredBySection = new Map<string, FeatureItem[]>();
        let totalFiltered = 0;
        for (const section of sections) {
            const filtered = this.filterFeatures(section.features);
            if (searchQuery && totalFiltered + filtered.length > MAX_SEARCH_RESULTS) {
                const remaining = Math.max(0, MAX_SEARCH_RESULTS - totalFiltered);
                filteredBySection.set(section.processType, filtered.slice(0, remaining));
                totalFiltered += filtered.length; // keep true count for display
                continue;
            }
            filteredBySection.set(section.processType, filtered);
            totalFiltered += filtered.length;
        }

        // _app renders the canonical <title> + <meta description> from the page's
        // seoHeaderTagsJson. Emit these here only as a FALLBACK for pages that lack
        // those authored tags — otherwise both render and we ship a duplicate
        // (Ahrefs "multiple meta descriptions" on the six *-tools category pages).
        const seoTags = ((this.props as any).seoHeaderTagsJson || []) as Array<any>;
        const hasSeoTitle = seoTags.some(e => e?.elementType === 'title');
        const hasSeoDescription = seoTags.some(e => e?.elementType === 'meta' && e?.elementAttributes?.name === 'description');
        // H1 + subtitle = the page's SSR page_seo (xconvert-web-ssr sources/page-data-overrides/<hub>.json,
        // tools/<process>.json); config.name/description only for a page that ships without SSR page data (/tools).
        const seo = (this.props as any).pageData?.page_seo || {};

        return (
            <>
                <Head>
                    {!hasSeoTitle && <title>{`${config.name} - Free Online ${config.name} | XConvert`}</title>}
                    {!hasSeoDescription && <meta name="description" content={config.metaDescription} />}
                </Head>

                <div className={styles['media-tools-page']}>
                    <header className={styles['page-header']}>
                        <div className={styles['page-icon']}>{config.icon}</div>
                        <h1 className={styles['page-title']}>{seo.title || config.name}</h1>
                        <p className={styles['page-description']}>{seo.description || config.description}</p>
                    </header>

                    {/* Search box for filtering tools */}
                    {totalTools > 10 && (
                        <div className={styles['search-container']}>
                            <input
                                type="text"
                                placeholder={`Search ${totalTools} tools...`}
                                defaultValue=""
                                onChange={this.handleSearchChange}
                                className={styles['search-input']}
                            />
                            {searchQuery && (
                                <button
                                    className={styles['clear-search']}
                                    onClick={this.clearSearch}
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    )}

                    <div className={styles['process-sections']}>
                        {sections.map(section => {
                            const filteredFeatures = filteredBySection.get(section.processType) || [];
                            const shouldGroup = section.features.length >= GROUP_THRESHOLD &&
                                section.features.some(f => f.sourceFormat);

                            // Skip sections with no matching results during search
                            if (searchQuery && filteredFeatures.length === 0) {
                                return null;
                            }

                            return (
                                <section key={section.processType} className={styles['process-section']}>
                                    <div className={styles['section-header']}>
                                        <span className={styles['section-icon']}>{section.icon}</span>
                                        <h2 className={styles['section-title']}>{section.titleOverride || `${section.displayName} Tools`}</h2>
                                        <span className={styles['section-count']}>
                                            {filteredFeatures.length}
                                            {searchQuery && filteredFeatures.length !== section.features.length
                                                ? ` of ${section.features.length}`
                                                : ''
                                            } tool{filteredFeatures.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    {filteredFeatures.length > 0 ? (
                                        shouldGroup && !searchQuery
                                            ? this.renderGroupedSection(section, filteredFeatures)
                                            : this.renderFlatSection(filteredFeatures)
                                    ) : (
                                        <div className={styles['no-features']}>
                                            No tools available in this category.
                                        </div>
                                    )}
                                </section>
                            );
                        })}

                        {sections.length === 0 && (
                            <div className={styles['no-features']}>
                                No tools available for this media type yet.
                            </div>
                        )}

                        {searchQuery && totalFiltered === 0 && (
                            <div className={styles['no-results']}>
                                No tools found matching "{searchQuery}".
                                <button
                                    className={styles['clear-search-link']}
                                    onClick={this.clearSearch}
                                >
                                    Clear search
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }
}
